import { useTranslate } from '@tolgee/react';
import { Modal, ProgressBar } from 'react-bootstrap';

export type CloudExportStatus = 'in_progress' | 'done' | 'failed';

export interface CloudExportItem {
  id: string;
  name: string;
  loaded: number;
  total: number | undefined;
  status: CloudExportStatus;
}

interface CloudExportProgressModalProps {
  show: boolean;
  items: CloudExportItem[];
}

const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
};

const CloudExportProgressModal = ({
  show,
  items
}: CloudExportProgressModalProps) => {
  const { t } = useTranslate();

  return (
    <Modal show={show} backdrop="static" centered>
      <Modal.Header>
        <Modal.Title as="h5">
          {t('lomavisCreator.additionalOptions.exportingFromCloud')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          {items.map((item) => {
            const percent =
              item.status === 'done'
                ? 100
                : item.total && item.total > 0
                ? Math.min(100, Math.round((item.loaded / item.total) * 100))
                : 0;
            const variant =
              item.status === 'failed'
                ? 'danger'
                : item.status === 'done'
                ? 'success'
                : 'primary';
            return (
              <div key={item.id}>
                <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
                  <span
                    className="small text-truncate"
                    title={item.name}
                    style={{ maxWidth: '70%' }}
                  >
                    {item.name}
                  </span>
                  <span className="small text-muted">
                    {item.status === 'failed'
                      ? t('lomavisCreator.additionalOptions.exportFailed')
                      : item.total
                      ? `${formatBytes(item.loaded)} / ${formatBytes(
                          item.total
                        )}`
                      : formatBytes(item.loaded)}
                  </span>
                </div>
                <ProgressBar
                  now={percent}
                  variant={variant}
                  animated={item.status === 'in_progress'}
                  style={{ height: '6px' }}
                />
              </div>
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CloudExportProgressModal;
